/**
 * Botão de anexos para o chat
 * 
 * Dropdown com opções de tipos de anexo (imagem, vídeo, documento, etc.)
 * Abre seletor de arquivo correspondente ao tipo escolhido
 * Suporta seleção de múltiplas imagens (limite WhatsApp: 30)
 */

import { useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Paperclip, Image, Video, FileText, MapPin, User, QrCode } from 'lucide-react';
import { toast } from 'sonner';

interface AttachmentButtonProps {
  /** Callback quando imagens são selecionadas (suporta múltiplas) */
  onImagesSelect: (files: File[]) => void;
  /** Callback quando um vídeo é selecionado */
  onVideoSelect?: (file: File) => void;
  /** Callback quando um documento é selecionado */
  onDocumentSelect?: (file: File) => void;
  /** Callback quando localização é selecionada */
  onLocationSelect?: () => void;
  /** Callback quando contato é selecionado */
  onContactSelect?: () => void;
  /** Callback quando PIX é selecionado */
  onPixSelect?: () => void;
  /** Se o botão está desabilitado */
  disabled?: boolean;
  /** Slug do provider da inbox (para ocultar opções não suportadas) */
  providerSlug?: string;
}

// Providers que NÃO suportam envio de localização e contato
const PROVIDERS_WITHOUT_LOCATION_CONTACT = ['meta-instagram', 'meta-facebook'];

// Providers que NÃO suportam envio de PIX (apenas WhatsApp não-oficial suporta)
const PROVIDERS_WITHOUT_PIX = ['meta-instagram', 'meta-facebook', 'meta-waba', 'notificame', 'chatwoot'];

// Tipos de documento aceitos
const ACCEPTED_DOCUMENT_TYPES = [
  '.pdf',
  '.doc', '.docx',
  '.xls', '.xlsx',
  '.ppt', '.pptx',
  '.txt', '.csv',
  '.zip', '.rar', '.7z',
].join(',');

// Tipos de vídeo aceitos (formatos suportados pelo WhatsApp)
const ACCEPTED_VIDEO_TYPES = 'video/mp4,video/webm,video/quicktime';

// Limite de 16MB para imagens no WhatsApp
const MAX_IMAGE_SIZE = 16 * 1024 * 1024;

// Limite de 20MB para vídeos
const MAX_VIDEO_SIZE = 20 * 1024 * 1024;

// Limite de 100MB para documentos na Z-API
const MAX_DOCUMENT_SIZE = 100 * 1024 * 1024;

// Limite de 30 imagens por envio (WhatsApp)
const MAX_IMAGES = 30;

export function AttachmentButton({
  onImagesSelect,
  onVideoSelect,
  onDocumentSelect,
  onLocationSelect,
  onContactSelect,
  onPixSelect,
  disabled = false,
  providerSlug,
}: AttachmentButtonProps) {
  // Verifica se o provider suporta localização e contato
  const supportsLocationContact = !providerSlug || !PROVIDERS_WITHOUT_LOCATION_CONTACT.includes(providerSlug);
  
  // Verifica se o provider suporta PIX (apenas WhatsApp não-oficial via Z-API/Evolution)
  const supportsPix = !providerSlug || !PROVIDERS_WITHOUT_PIX.includes(providerSlug);
  
  // Opções de localização e contato (ocultas para Instagram/Facebook)
  const showLocationOption = supportsLocationContact && !!onLocationSelect;
  const showContactOption = supportsLocationContact && !!onContactSelect;
  // Opção de PIX (oculta para providers que não suportam)
  const showPixOption = supportsPix && !!onPixSelect;
  // Refs para os inputs de arquivo ocultos
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  // Handler para seleção de imagem
  const handleImageClick = () => {
    imageInputRef.current?.click();
  };

  // Handler para seleção de vídeo
  const handleVideoClick = () => {
    videoInputRef.current?.click();
  };

  // Handler para seleção de documento
  const handleDocumentClick = () => {
    documentInputRef.current?.click();
  };

  // Handler quando arquivos de imagem são selecionados
  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      
      // Valida quantidade máxima
      if (fileArray.length > MAX_IMAGES) {
        toast.error(`Máximo de ${MAX_IMAGES} imagens por envio`);
        event.target.value = '';
        return;
      }

      // Filtra apenas imagens válidas
      const validFiles: File[] = [];
      const errors: string[] = [];

      fileArray.forEach((file, index) => {
        // Valida tipo de arquivo
        if (!file.type.startsWith('image/')) {
          errors.push(`Arquivo ${index + 1}: não é uma imagem`);
          return;
        }
        
        // Valida tamanho (16MB limite do WhatsApp)
        if (file.size > MAX_IMAGE_SIZE) {
          errors.push(`${file.name}: maior que 16MB`);
          return;
        }
        
        validFiles.push(file);
      });

      // Mostra erros se houver
      if (errors.length > 0) {
        toast.error(errors.slice(0, 3).join('\n') + (errors.length > 3 ? `\n...e mais ${errors.length - 3} erros` : ''));
      }

      // Envia arquivos válidos
      if (validFiles.length > 0) {
        onImagesSelect(validFiles);
      }
    }
    
    // Limpa o input para permitir selecionar os mesmos arquivos novamente
    event.target.value = '';
  };

  // Handler quando arquivo de vídeo é selecionado
  const handleVideoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Valida tipo de arquivo
      if (!file.type.startsWith('video/')) {
        toast.error('Arquivo selecionado não é um vídeo válido');
        event.target.value = '';
        return;
      }
      
      // Valida tamanho (20MB limite)
      if (file.size > MAX_VIDEO_SIZE) {
        toast.error('Vídeo muito grande. O limite é 20MB.');
        event.target.value = '';
        return;
      }
      
      onVideoSelect?.(file);
    }
    
    // Limpa o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = '';
  };

  // Handler quando arquivo de documento é selecionado
  const handleDocumentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Valida tamanho (100MB limite da Z-API)
      if (file.size > MAX_DOCUMENT_SIZE) {
        toast.error('Documento muito grande. O limite é 100MB.');
        return;
      }
      
      onDocumentSelect?.(file);
    }
    
    // Limpa o input para permitir selecionar o mesmo arquivo novamente
    event.target.value = '';
  };

  return (
    <>
      {/* Input de arquivo oculto para imagens (múltiplas) */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={handleImageChange}
      />

      {/* Input de arquivo oculto para vídeos */}
      <input
        ref={videoInputRef}
        type="file"
        accept={ACCEPTED_VIDEO_TYPES}
        className="hidden"
        onChange={handleVideoChange}
      />

      {/* Input de arquivo oculto para documentos */}
      <input
        ref={documentInputRef}
        type="file"
        accept={ACCEPTED_DOCUMENT_TYPES}
        className="hidden"
        onChange={handleDocumentChange}
      />

      {/* Dropdown de anexos - Estilo WhatsApp Web */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            disabled={disabled}
            className="h-9 w-9 text-foreground hover:text-primary"
          >
            <Paperclip className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="start" 
          side="top"
          className="w-56 p-2 rounded-xl shadow-xl border-0 bg-popover"
        >
          {/* Imagens */}
          <DropdownMenuItem 
            onClick={handleImageClick}
            className="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors focus:bg-accent"
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30">
              <Image className="h-5 w-5 text-blue-500" />
            </div>
            <span className="text-sm font-medium">Imagens</span>
          </DropdownMenuItem>

          {/* Vídeos */}
          <DropdownMenuItem 
            onClick={handleVideoClick}
            disabled={!onVideoSelect}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors focus:bg-accent ${!onVideoSelect ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30">
              <Video className="h-5 w-5 text-red-500" />
            </div>
            <span className="text-sm font-medium">Vídeos</span>
          </DropdownMenuItem>

          {/* Documento */}
          <DropdownMenuItem 
            onClick={handleDocumentClick}
            disabled={!onDocumentSelect}
            className={`flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors focus:bg-accent ${!onDocumentSelect ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30">
              <FileText className="h-5 w-5 text-purple-500" />
            </div>
            <span className="text-sm font-medium">Documento</span>
          </DropdownMenuItem>

          {/* Localização - Oculto para Instagram/Facebook */}
          {showLocationOption && (
            <DropdownMenuItem 
              onClick={onLocationSelect}
              className="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors focus:bg-accent"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <MapPin className="h-5 w-5 text-emerald-500" />
              </div>
              <span className="text-sm font-medium">Localização</span>
            </DropdownMenuItem>
          )}

          {/* Contato - Oculto para Instagram/Facebook */}
          {showContactOption && (
            <DropdownMenuItem 
              onClick={onContactSelect}
              className="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors focus:bg-accent"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-sky-100 dark:bg-sky-900/30">
                <User className="h-5 w-5 text-sky-500" />
              </div>
              <span className="text-sm">Contato</span>
            </DropdownMenuItem>
          )}

          {/* PIX - Oculto para providers que não suportam (Meta WABA, Instagram, etc.) */}
          {showPixOption && (
            <DropdownMenuItem 
              onClick={onPixSelect}
              className="flex items-center gap-3 px-3 py-3 rounded-lg cursor-pointer transition-colors focus:bg-accent"
            >
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                <QrCode className="h-5 w-5 text-emerald-500" />
              </div>
              <span className="text-sm">PIX</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
}
